import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Route, Routes, useNavigate } from "react-router-dom";
import SignUpContextProvider from "../../../../context/SignUpContext";
import { SubscriptionPlan } from "../../../signup/SignUpApi";
import { useStickyState } from "../../../../hooks/useStickyState";
import "../../../signup/SignUp.scss";
import { EAggregators } from "utils/aggregators";
import { SignupAggregator } from "./contact/SignupAggregator";
import loadable from "@loadable/component";
import { SignUpConfirmationV2 } from "../../../signup/SignUpConfirmationV2";
import { useContentfulContext } from "context/ContentfulContext";
import { createBrowserHistory } from "history";
import { useScrollToTop } from "hooks/useScroll";
import { SignupDevNav } from "./SignupDevNav";
/**
 * Chunk names carry a `Uk` suffix.
 *
 * Webpack merges every module sharing a `webpackChunkName` into one chunk, and
 * `components/signup/SignUpV2.tsx` names its US steps identically. While the
 * names matched, each UK step also shipped its US counterpart's stylesheet —
 * which is where the legacy blue buttons and mismatched field corners in this
 * flow came from. The steps that genuinely share a module with the US app
 * (payment, verify phone, schedule) keep their original names.
 */
const SignUpCreatePasswordV2 = loadable(
  () => import(/* webpackChunkName: "SignUpCreatePasswordUk" */ "./password/SignUpCreatePasswordV2"),
  {
    resolveComponent: (components) => components.SignUpCreatePasswordV2,
  }
) as any;

const SignUpChangePaymentDateV2 = loadable(
  () => import(/* webpackChunkName: "SignUpChangePaymentDateUk" */ "./SignUpChangePaymentDateV2"),
  {
    resolveComponent: (components) => components.SignUpChangePaymentDateV2,
  }
) as any;

const SignUpPaymentV2 = loadable(
  () => import(/* webpackChunkName: "SignUpPaymentV2" */ "../../../signup/SignUpPaymentV2"),
  {
    resolveComponent: (components) => components.SignUpPaymentV2,
  }
) as any;

const SignUpSubscriptionV2 = loadable(
  () => import(/* webpackChunkName: "SignUpSubscriptionUk" */ "./plans/SignUpSubscriptionV2"),
  {
    resolveComponent: (components) => components.SignUpSubscriptionV2,
  }
) as any;

const ImportantInformation = loadable(
  () => import(/* webpackChunkName: "ImportantInformation" */ "./importantInformation/ImportantInformation"),
  {
    resolveComponent: (components) => components.ImportantInformation,
  }
) as any;

const SignUpAboutYourDataV2 = loadable(
  () => import(/* webpackChunkName: "SignUpAboutYourDataUk" */ "./aboutYourData/SignUpAboutYourDataV2"),
  {
    resolveComponent: (components) => components.SignUpAboutYourDataV2,
  }
) as any;

const SignUpPhoneNumber = loadable(
  () => import(/* webpackChunkName: "SignUpPhoneNumberUk" */ "./contact/SignUpPhoneNumber"),
  {
    resolveComponent: (components) => components.SignUpPhoneNumber,
  }
) as any;

const SignUpEmail = loadable(() => import(/* webpackChunkName: "SignUpEmailUk" */ "./contact/SignUpEmail"), {
  resolveComponent: (components) => components.SignUpEmail,
}) as any;

const SignUpLegalNameV2 = loadable(() => import(/* webpackChunkName: "SignUpLegalNameUk" */ "./SignUpLegalNameV2"), {
  resolveComponent: (components) => components.SignUpLegalNameV2,
}) as any;

const SignUpVerifyPhone = loadable(
  () => import(/* webpackChunkName: "SignUpVerifyPhone" */ "../../../signup/contact/SignUpVerifyPhone"),
  {
    resolveComponent: (components) => components.SignUpVerifyPhone,
  }
) as any;

const SignUpReferralsV2 = loadable(
  () => import(/* webpackChunkName: "SignUpReferralsUk" */ "./referrals/SignUpReferralsV2"),
  {
    resolveComponent: (components) => components.SignUpReferralsV2,
  }
) as any;

const SignUpSchedulePayment = loadable(
  () => import(/* webpackChunkName: "SignUpSchedulePayment" */ "../../../signup/schedulepayment/SignUpSchedulePayment")
) as any;

const scrollToTop = useScrollToTop();
interface ISignUp {
  setHeaderClass: (headerClass: string | null) => void;
  setWrapperClass: (wrapperClass: string | null) => void;
  setBypassAuthCheck: (bypassAuthCheck: boolean) => void;
  setDisplayBackButton: (displayBackButton: boolean) => void;
  setAggregator: (aggregator: EAggregators | undefined) => void;
}

interface IUseSignUp {
  layout: string;
  subscriptionPlan: SubscriptionPlan | undefined;
  signUpStep: number;
  setSignUpStep: (signUpStep: number) => void;
  setSubscriptionPlan: (subscriptionPlan: SubscriptionPlan | undefined) => void;
  isBeforeSubscriptionPlansPage: boolean;
}

export const SIGN_UP_V2_INDEX_ROUTE_STEP_NO = 0;
export const SIGN_UP_V2_PHONE_NUMBER_STEP_NO = 1;
export const SIGN_UP_V2_VERIFY_PHONE_NUMBER_STEP_NO = 2;
export const SIGN_UP_V2_CREATE_PASSWORD_STEP_NO = 3;
export const SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO = 4;
export const SIGN_UP_V2_LEGAL_NAME_STEP_NO = 5;
export const SIGN_UP_V2_REFERRALS_STEP_NO = 6;
export const SIGN_UP_V2_IMPORTANT_INFORMATION_STEP_NO = 8;
export const SIGN_UP_V2_ABOUT_YOUR_DATA_STEP_NO = 7;
export const LOADING_CREDIT_FILE_STEP_NO = 999;
export const SIGN_UP_V2_PAYMENT_STEP_NO = 9;
export const SIGN_UP_V2_CHANGE_PAYMENT_DATE_STEP_NO = 10;
export const SIGN_UP_V2_CONFIRMATION_STEP_NO = 11;
export const SIGN_UP_V2_SCHEDULE_CONFIRMATION_STEP_NO = 12;

export const SIGN_UP_V2_INDEX_ROUTE_URL = "/signup";
export const SIGN_UP_V2_PHONE_NUMBER_URL = "/signup/phone";
export const SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL = "/signup/smsverification";
export const SIGN_UP_V2_LEGALNAME_URL = "/signup/legal";
export const SIGN_UP_V2_SUBSCRIPTION_PLANS_URL = "/signup/subscription";
export const SIGN_UP_V2_CREATE_PASSWORD_URL = "/signup/password";
export const SIGN_UP_V2_REFERRALS_URL = "/signup/referrals";
export const SIGN_UP_V2_IMPORTANT_INFORMATION_URL = "/signup/important-information";
export const SIGN_UP_V2_ABOUT_YOUR_DATA_URL = "/signup/aboutyourdata";
export const SIGN_UP_V2_PAYMENT_URL = "/signup/payment";
export const SIGN_UP_V2_CHANGE_PAYMENT_DATE_URL = "/signup/paymentdate";
export const SIGN_UP_V2_CONFIRMATION_URL = "/signup/confirmation";
export const SIGN_UP_V2_SCHEDULE_URL = "/signup/schedule";
export const SIGN_UP_V2_TOTALLY_MONEY_URL = "/signup/totallymoney";
export const SIGN_UP_V2_CREDIT_KARMA_URL = "/signup/creditkarma";
export const SIGN_UP_V2_CLEARSCORE_URL = "/signup/clearscore";

enum SignupPaths {
  EMAIL = "email",
  PHONE_NUMBER = "phone",
  ADDRESS = "address",
  CONTACT_INFO = "contactinfo",
  PASSWORD = "password",
  REFERRALS = "referrals",
  SCHEDULE = "schedule",
  IMPORTANT_INFORMATION = "important-information",
  ABOUT_YOUR_DATA = "aboutyourdata",
  SUBSCRIPTION = "subscription",
  PAYMENT = "payment",
  PAYMENT_DATE = "paymentdate",
  CONFIRMATION = "confirmation",
  LEGAL = "legal",
  PHONE_VERIFICATION = "smsverification",
  TOTALLY_MONEY = "totallymoney",
  CLEARSCORE = "clearscore",
  CREDIT_KARMA = "creditkarma",
}
/* eslint-disable @typescript-eslint/no-unused-vars */
const useSignUp = ({ setHeaderClass, setWrapperClass, setDisplayBackButton }: any): IUseSignUp => {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [isBeforeSubscriptionPlansPage, setIsBeforeSubscriptionPlansPage] = useState(false);
  const [signUpStep, setSignUpStep] = useStickyState<number>(0, "signUpStep");
  const [styleClasses, setStyleClasses] = useState<any>({
    headerClass: "",
    wrapperClass: "",
    layout: "welcome-layout",
  });
  const [subscriptionPlan, setSubscriptionPlan] = useStickyState<SubscriptionPlan | undefined>(
    undefined,
    "subscriptionPlan"
  );
  const navigate = useNavigate();
  const history = createBrowserHistory();

  scrollToTop();
  useEffect(() => {
    history.listen((update: any) => {
      if (update.action === "POP") {
        if (window.location.pathname === SIGN_UP_V2_LEGALNAME_URL) {
          navigate(SIGN_UP_V2_REFERRALS_URL);
        } else if (window.location.pathname === SIGN_UP_V2_REFERRALS_URL) {
          navigate(SIGN_UP_V2_ABOUT_YOUR_DATA_URL);
        }
      }
    });
  }, []);

  useEffect(() => {
    const styleClasses: any = getClassNames();
    setStyleClasses(styleClasses);
    const { headerClass, wrapperClass } = styleClasses;
    setHeaderClass(headerClass);
    setWrapperClass(wrapperClass);
    const displayBackButton: boolean =
      signUpStep > 0 &&
      signUpStep !== SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO &&
      signUpStep !== SIGN_UP_V2_SCHEDULE_URL &&
      signUpStep !== SIGN_UP_V2_CHANGE_PAYMENT_DATE_STEP_NO &&
      signUpStep !== SIGN_UP_V2_REFERRALS_STEP_NO &&
      signUpStep !== SIGN_UP_V2_ABOUT_YOUR_DATA_STEP_NO;

    setDisplayBackButton(displayBackButton);
    setIsBeforeSubscriptionPlansPage(signUpStep < SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO);
  }, [signUpStep]);

  const getClassNames = (): any => {
    switch (signUpStep) {
      case SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO:
        return { headerClass: "", wrapperClass: "", layout: "subscriptions-layout" };
      case LOADING_CREDIT_FILE_STEP_NO:
        return { headerClass: "d-none", wrapperClass: "", layout: "welcome-layout welcome-loading-layout" };
      default:
        return { headerClass: "", wrapperClass: "", layout: "welcome-layout" };
    }
  };

  // useEffect(() => {
  //   if (window.location.pathname.endsWith("/signup")) {
  //     return navigate("/login");
  //   }
  // }, [window.location.pathname]);

  const layout: string = styleClasses?.layout || "welcome-layout";

  return {
    layout,
    setSignUpStep,
    isBeforeSubscriptionPlansPage,
    subscriptionPlan,
    signUpStep,
    setSubscriptionPlan,
  };
};

export const SignUpV2 = ({
  setHeaderClass,
  setWrapperClass,
  setBypassAuthCheck,
  setDisplayBackButton,
  setAggregator,
}: ISignUp) => {
  const { layout, signUpStep, subscriptionPlan, setSignUpStep, setSubscriptionPlan } = useSignUp({
    setHeaderClass,
    setWrapperClass,
    setDisplayBackButton,
  });

  const { content } = useContentfulContext();
  const { displayImportantInformationPage }: any = content ?? { displayImportantInformationPage: false };

  return (
    <SignUpContextProvider>
      <div className={layout}>
        <SignupDevNav setSignUpStep={setSignUpStep} />
        <Container className="pb-3">
          <Row className="justify-content-center">
            <Col>
              <Routes>
                <Route
                  path={SignupPaths.TOTALLY_MONEY}
                  element={
                    <SignupAggregator
                      aggregator={EAggregators.TOTALLY_MONEY_STORE_CARD}
                      setSignUpStep={setSignUpStep}
                      setAggregator={setAggregator}
                    />
                  }
                />
                <Route
                  path={SignupPaths.CLEARSCORE}
                  element={
                    <SignupAggregator
                      aggregator={EAggregators.CLEARSCORE_STORE_CARD}
                      setSignUpStep={setSignUpStep}
                      setAggregator={setAggregator}
                    />
                  }
                />
                <Route
                  path={SignupPaths.CREDIT_KARMA}
                  element={
                    <SignupAggregator
                      aggregator={EAggregators.CREDIT_KARMA_STORE_CARD}
                      setSignUpStep={setSignUpStep}
                      setAggregator={setAggregator}
                    />
                  }
                />
                <Route index element={<SignUpEmail setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.LEGAL} element={<SignUpLegalNameV2 setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.PHONE_NUMBER} element={<SignUpPhoneNumber setSignUpStep={setSignUpStep} />} />
                <Route
                  path={SignupPaths.PHONE_VERIFICATION}
                  element={<SignUpVerifyPhone setSignUpStep={setSignUpStep} />}
                />
                <Route path={SignupPaths.PASSWORD} element={<SignUpCreatePasswordV2 setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.REFERRALS} element={<SignUpReferralsV2 setSignUpStep={setSignUpStep} />} />
                <Route
                  path={SignupPaths.SCHEDULE}
                  element={<SignUpSchedulePayment setSignUpStep={setSignUpStep} subscriptionPlan={subscriptionPlan} />}
                />
                {displayImportantInformationPage && (
                  <Route
                    path={SignupPaths.IMPORTANT_INFORMATION}
                    element={<ImportantInformation setSignUpStep={setSignUpStep} />}
                  />
                )}
                <Route
                  path={SignupPaths.ABOUT_YOUR_DATA}
                  element={
                    <SignUpAboutYourDataV2 setSignUpStep={setSignUpStep} setBypassAuthCheck={setBypassAuthCheck} />
                  }
                />
                <Route
                  path={SignupPaths.SUBSCRIPTION}
                  element={
                    <SignUpSubscriptionV2
                      setSignUpStep={setSignUpStep}
                      setBypassAuthCheck={setBypassAuthCheck}
                      setSubscriptionPlan={setSubscriptionPlan}
                    />
                  }
                />
                <Route
                  path={SignupPaths.PAYMENT}
                  element={<SignUpPaymentV2 subscriptionPlan={subscriptionPlan!} setSignUpStep={setSignUpStep} />}
                />
                <Route
                  path={SignupPaths.PAYMENT_DATE}
                  element={<SignUpChangePaymentDateV2 setSignUpStep={setSignUpStep} signUpStep={signUpStep} />}
                />
                {/* TODO: subscriptionPlan must be in signup context */}
                <Route
                  path={SignupPaths.CONFIRMATION}
                  element={<SignUpConfirmationV2 setSignUpStep={setSignUpStep} />}
                />
              </Routes>
            </Col>
          </Row>
        </Container>
      </div>
    </SignUpContextProvider>
  );
};
